/**
 * Portfolio Hierarchy View - Sprint 14
 * PM-1: Portfolio Structure & Strategy
 * 
 * Three-level tree: Portfolio → Program → Project
 * With strategy themes, OKRs, and drill-down navigation
 * 
 * Enhanced with:
 * - Search across all levels
 * - Quick filters by status/health
 * - Expand/collapse all controls
 * - Breadcrumb navigation
 * - Summary statistics
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight, ChevronDown, Folder, FolderOpen, FileText,
  Target, Plus, Edit2, Loader2, Building, Users, Tag,
  TrendingUp, AlertCircle, RefreshCw, BarChart3, Settings,
  CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Filter, ChevronsUpDown, ChevronsUp, ChevronsDown, X,
  DollarSign, ClipboardList, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AddToFavoritesButton } from '../components/ProjectFavoritesPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
  const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
  const activeTenantId = impersonatedTenantId || userTenantId;
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (activeTenantId) {
    headers['X-Tenant-ID'] = activeTenantId;
  }
  return headers;
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    active: { color: 'bg-green-100 text-green-700', label: 'Active' },
    on_hold: { color: 'bg-amber-100 text-amber-700', label: 'On Hold' },
    completed: { color: 'bg-blue-100 text-blue-700', label: 'Completed' },
    cancelled: { color: 'bg-gray-100 text-gray-500', label: 'Cancelled' }
  };
  
  const cfg = config[status] || config.active;
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// Health Badge Component
const HealthBadge = ({ health }) => {
  const config = {
    green: { color: 'bg-green-500', label: 'On Track' },
    amber: { color: 'bg-amber-500', label: 'At Risk' },
    red: { color: 'bg-red-500', label: 'Critical' }
  };
  
  const cfg = config[health] || config.green;
  
  return (
    <span className={`w-3 h-3 rounded-full inline-block ${cfg.color}`} title={cfg.label} />
  );
};

// Strategy Theme Tag
const ThemeTag = ({ theme }) => {
  const typeColors = {
    strategic: 'bg-purple-100 text-purple-700 border-purple-200',
    operational: 'bg-blue-100 text-blue-700 border-blue-200',
    compliance: 'bg-red-100 text-red-700 border-red-200'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded border text-xs ${typeColors[theme.theme_type] || typeColors.operational}`}>
      <Target className="w-3 h-3 inline mr-1" />
      {theme.name}
    </span>
  );
};

// OKR Badge Component
const OKRBadge = ({ okr }) => {
  const progressColor = okr.progress >= 80 ? 'bg-green-500' : okr.progress >= 50 ? 'bg-amber-500' : 'bg-red-500';
  
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-xs">
      <Target className="w-3 h-3 text-blue-600" />
      <span className="text-blue-700">{okr.name}</span>
      <div className="w-8 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${progressColor}`} style={{ width: `${okr.progress}%` }} />
      </div>
      <span className="text-blue-600">{okr.progress}%</span>
    </div>
  );
};

// Tree Node Component with Inline Editing
const TreeNode = ({ 
  node, 
  level = 0, 
  expanded, 
  onToggle, 
  onSelect, 
  onEdit,
  onInlineEdit,
  selectedId 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(node.name);
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded[node.id];
  const isSelected = selectedId === node.id;
  
  const IconClosed = level === 0 ? Folder : level === 1 ? FolderOpen : FileText;
  const IconOpen = level === 0 ? FolderOpen : level === 1 ? FolderOpen : FileText;
  
  const bgColors = {
    0: isSelected ? 'bg-purple-50 border-purple-200' : 'hover:bg-gray-50',
    1: isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50',
    2: isSelected ? 'bg-green-50 border-green-200' : 'hover:bg-gray-50'
  };
  
  const handleInlineEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditName(node.name);
  };
  
  const handleSaveEdit = async (e) => {
    e.stopPropagation();
    if (editName.trim() && editName !== node.name) {
      await onInlineEdit?.(node.id, node.type, { name: editName.trim() });
    }
    setIsEditing(false);
  };
  
  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditName(node.name);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit(e);
    } else if (e.key === 'Escape') {
      handleCancelEdit(e);
    }
  };
  
  return (
    <div className="group">
      <div 
        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border rounded-lg mb-1 ${bgColors[level]}`}
        style={{ marginLeft: `${level * 24}px` }}
        onClick={() => !isEditing && onSelect(node)}
        data-testid={`tree-node-${node.id}`}
      >
        {/* Expand/Collapse */}
        {hasChildren ? (
          <button 
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}
        
        {/* Icon */}
        {isExpanded ? (
          <IconOpen className={`w-5 h-5 ${level === 0 ? 'text-purple-500' : level === 1 ? 'text-blue-500' : 'text-green-500'}`} />
        ) : (
          <IconClosed className={`w-5 h-5 ${level === 0 ? 'text-purple-500' : level === 1 ? 'text-blue-500' : 'text-green-500'}`} />
        )}
        
        {/* Name & Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="px-2 py-0.5 border rounded text-sm w-48"
                  autoFocus
                  data-testid="inline-edit-input"
                />
                <button onClick={handleSaveEdit} className="p-1 text-green-600 hover:bg-green-50 rounded">
                  <CheckCircle className="w-4 h-4" />
                </button>
                <button onClick={handleCancelEdit} className="p-1 text-red-600 hover:bg-red-50 rounded">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <span 
                  className="font-medium text-gray-900 truncate"
                  onDoubleClick={handleInlineEdit}
                  title="Double-click to edit"
                >
                  {node.name}
                </span>
                {node.health && <HealthBadge health={node.health} />}
              </>
            )}
          </div>
          {/* Strategy Themes / OKRs */}
          {level === 0 && node.strategy_themes && node.strategy_themes.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {node.strategy_themes.slice(0, 3).map((theme, idx) => (
                <ThemeTag key={idx} theme={theme} />
              ))}
              {node.strategy_themes.length > 3 && (
                <span className="text-xs text-gray-500">+{node.strategy_themes.length - 3} more</span>
              )}
            </div>
          )}
          {/* OKRs for programs */}
          {level === 1 && node.okrs && node.okrs.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {node.okrs.slice(0, 2).map((okr, idx) => (
                <OKRBadge key={idx} okr={okr} />
              ))}
            </div>
          )}
        </div>
        
        {/* Status & Actions */}
        <div className="flex items-center gap-2">
          <StatusBadge status={node.status} />
          <span className="text-xs text-gray-400">
            {node.type === 'portfolio' && `${node.children?.length || 0} programs`}
            {node.type === 'program' && `${node.children?.length || 0} projects`}
          </span>
          {/* Favorite button for projects */}
          {node.type === 'project' && (
            <div onClick={(e) => e.stopPropagation()}>
              <AddToFavoritesButton project={{ id: node.id, name: node.name }} />
            </div>
          )}
          {!isEditing && (
            <button
              onClick={handleInlineEdit}
              className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit name"
            >
              <Edit2 className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="ml-4">
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              onEdit={onEdit}
              onInlineEdit={onInlineEdit}
              selectedId={selectedId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Details Panel Component
const DetailsPanel = ({ node, onClose, onNavigate }) => {
  if (!node) return null;
  
  return (
    <div className="bg-white rounded-lg border shadow-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg text-gray-900">{node.name}</h3>
        <StatusBadge status={node.status} />
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase">Type</label>
          <p className="text-sm text-gray-900 capitalize">{node.type}</p>
        </div>
        
        {node.description && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
            <p className="text-sm text-gray-700">{node.description}</p>
          </div>
        )}
        
        {node.owner_name && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Owner</label>
            <p className="text-sm text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              {node.owner_name}
            </p>
          </div>
        )}
        
        {node.budget > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Budget</label>
            <p className="text-sm text-gray-900 font-semibold">
              ${node.budget?.toLocaleString()}
            </p>
          </div>
        )}
        
        {node.strategy_themes && node.strategy_themes.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Strategy Themes</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {node.strategy_themes.map((theme, idx) => (
                <ThemeTag key={idx} theme={theme} />
              ))}
            </div>
          </div>
        )}
        
        {node.children && node.children.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">
              {node.type === 'portfolio' ? 'Programs' : 'Projects'}
            </label>
            <div className="mt-2 space-y-1">
              {node.children.slice(0, 5).map(child => (
                <div 
                  key={child.id}
                  className="text-sm flex items-center gap-2 text-gray-700 hover:text-blue-600 cursor-pointer"
                  onClick={() => onNavigate(child)}
                >
                  <ChevronRight className="w-4 h-4" />
                  {child.name}
                  {child.health && <HealthBadge health={child.health} />}
                </div>
              ))}
              {node.children.length > 5 && (
                <p className="text-xs text-gray-400">+{node.children.length - 5} more</p>
              )}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="pt-4 border-t space-y-2">
          {/* Primary Actions Row */}
          <div className="flex gap-2">
            {node.type === 'project' && (
              <button
                onClick={() => onNavigate({ type: 'project-dashboard', id: node.id })}
                className="flex-1 py-2 bg-blue-50 text-blue-700 rounded font-medium hover:bg-blue-100 flex items-center justify-center gap-2"
                data-testid={`hierarchy-dashboard-${node.id}`}
              >
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </button>
            )}
            <button
              onClick={() => onNavigate({ type: 'settings', id: node.id, nodeType: node.type })}
              className="flex-1 py-2 border rounded font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
              data-testid={`hierarchy-settings-${node.id}`}
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
          </div>
          
          {/* Quick Navigation Row - Requirements & Financial */}
          {node.type === 'project' && (
            <div className="flex gap-2">
              <button
                onClick={() => onNavigate({ type: 'requirements', id: node.id, projectName: node.name })}
                className="flex-1 py-2 bg-purple-50 text-purple-700 rounded font-medium hover:bg-purple-100 flex items-center justify-center gap-2"
                data-testid={`hierarchy-requirements-${node.id}`}
              >
                <ClipboardList className="w-4 h-4" />
                Requirements
              </button>
              <button
                onClick={() => onNavigate({ type: 'financial', id: node.id, projectName: node.name })}
                className="flex-1 py-2 bg-green-50 text-green-700 rounded font-medium hover:bg-green-100 flex items-center justify-center gap-2"
                data-testid={`hierarchy-financial-${node.id}`}
              >
                <DollarSign className="w-4 h-4" />
                Financial
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Portfolio/Program Modal
const CreateModal = ({ isOpen, onClose, type, parentId, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    status: 'active',
    budget: 0
  });
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      let endpoint;
      let payload;
      
      if (type === 'portfolio') {
        endpoint = '/portfolio-mgmt/portfolios';
        payload = form;
      } else if (type === 'program') {
        endpoint = '/portfolio-mgmt/programs';
        payload = { ...form, portfolio_id: parentId };
      } else if (type === 'project') {
        // Create project - uses projects API with program_id
        endpoint = '/projects';
        payload = { 
          ...form, 
          program_id: parentId,
          // Ensure required project fields have defaults
          priority: 'medium',
          health_status: 'on_track'
        };
      } else {
        toast.error('Invalid type');
        return;
      }
      
      await axios.post(`${API_BASE}${endpoint}`, payload, { headers: getAuthHeaders() });
      toast.success(`${type} created successfully`);
      onSuccess();
      onClose();
      setForm({ name: '', description: '', status: 'active', budget: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to create ${type}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4 capitalize">Create {type}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
              required
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded px-3 py-2 mt-1"
              rows={3}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700">Budget</label>
            <input
              type="number"
              value={form.budget}
              onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) || 0 })}
              className="w-full border rounded px-3 py-2 mt-1"
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// R3-001: Move Project/Program Modal
const MoveModal = ({ isOpen, onClose, item, programs, portfolios, onSuccess }) => {
  const [targetId, setTargetId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  if (!isOpen || !item) return null;
  
  const isProject = item.type === 'project';
  const options = isProject ? programs : portfolios;
  const targetLabel = isProject ? 'Program' : 'Portfolio';
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const endpoint = isProject 
        ? `${API_BASE}/portfolio-mgmt/projects/${item.id}/move`
        : `${API_BASE}/portfolio-mgmt/programs/${item.id}/move`;
      
      const params = isProject 
        ? { target_program_id: targetId || null }
        : { target_portfolio_id: targetId || null };
      
      await axios.post(endpoint, null, { 
        headers: getAuthHeaders(),
        params 
      });
      
      toast.success(`${isProject ? 'Project' : 'Program'} ${targetId ? 'moved' : 'unassigned'} successfully`);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Move failed:', err);
      toast.error(`Failed to move ${isProject ? 'project' : 'program'}`);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Move {isProject ? 'Project' : 'Program'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Moving: <span className="font-medium">{item.name}</span>
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target {targetLabel}
            </label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Unassign (no {targetLabel.toLowerCase()}) --</option>
              {options.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Select a {targetLabel.toLowerCase()} or leave empty to unassign
            </p>
          </div>
          
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {targetId ? 'Move' : 'Unassign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const PortfolioHierarchy = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [createModal, setCreateModal] = useState({ open: false, type: null, parentId: null });
  const [moveModal, setMoveModal] = useState({ open: false, item: null });
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [healthFilter, setHealthFilter] = useState(searchParams.get('health') || 'all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Extract all programs and portfolios for move modal
  const allPrograms = useMemo(() => {
    const programs = [];
    hierarchy.forEach(portfolio => {
      (portfolio.children || []).forEach(program => {
        programs.push({ id: program.id, name: `${portfolio.name} > ${program.name}` });
      });
    });
    return programs;
  }, [hierarchy]);
  
  const allPortfolios = useMemo(() => {
    return hierarchy.map(p => ({ id: p.id, name: p.name }));
  }, [hierarchy]);
  
  // Calculate statistics
  const stats = useMemo(() => {
    let portfolioCount = 0;
    let programCount = 0;
    let projectCount = 0;
    let atRiskCount = 0;
    let criticalCount = 0;
    
    const countNodes = (nodes, level = 0) => {
      nodes.forEach(node => {
        if (level === 0) portfolioCount++;
        else if (level === 1) programCount++;
        else if (level === 2) projectCount++;
        
        if (node.health === 'amber' || node.health === 'At Risk') atRiskCount++;
        if (node.health === 'red' || node.health === 'Critical') criticalCount++;
        
        if (node.children) countNodes(node.children, level + 1);
      });
    };
    countNodes(hierarchy);
    
    return { portfolioCount, programCount, projectCount, atRiskCount, criticalCount };
  }, [hierarchy]);
  
  // Filtered hierarchy based on search and filters
  const filteredHierarchy = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    
    const filterNode = (node, level = 0) => {
      // Check if this node matches search
      const matchesSearch = !searchTerm || 
        node.name?.toLowerCase().includes(searchLower) ||
        node.description?.toLowerCase().includes(searchLower);
      
      // Check if this node matches filters
      const matchesStatus = statusFilter === 'all' || node.status === statusFilter;
      const matchesHealth = healthFilter === 'all' || 
        node.health === healthFilter ||
        (healthFilter === 'at-risk' && (node.health === 'amber' || node.health === 'At Risk')) ||
        (healthFilter === 'critical' && (node.health === 'red' || node.health === 'Critical'));
      
      // Filter children recursively
      const filteredChildren = node.children 
        ? node.children.map(child => filterNode(child, level + 1)).filter(Boolean)
        : [];
      
      // Include node if it matches OR has matching children
      if (matchesSearch && matchesStatus && matchesHealth) {
        return { ...node, children: filteredChildren };
      }
      
      // Include node if any children match (show parent path to results)
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      
      return null;
    };
    
    return hierarchy.map(node => filterNode(node)).filter(Boolean);
  }, [hierarchy, searchTerm, statusFilter, healthFilter]);
  
  // Expand/collapse all handlers
  const expandAll = useCallback(() => {
    const newExpanded = {};
    const addToExpanded = (nodes) => {
      nodes.forEach(node => {
        newExpanded[node.id] = true;
        if (node.children) addToExpanded(node.children);
      });
    };
    addToExpanded(hierarchy);
    setExpanded(newExpanded);
  }, [hierarchy]);
  
  const collapseAll = useCallback(() => {
    setExpanded({});
  }, []);
  
  const expandToLevel = useCallback((level) => {
    const newExpanded = {};
    const addToExpanded = (nodes, currentLevel) => {
      nodes.forEach(node => {
        if (currentLevel < level) {
          newExpanded[node.id] = true;
        }
        if (node.children) addToExpanded(node.children, currentLevel + 1);
      });
    };
    addToExpanded(hierarchy, 0);
    setExpanded(newExpanded);
  }, [hierarchy]);
  
  // Load hierarchy
  const loadHierarchy = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/portfolio-mgmt/hierarchy`, { headers: getAuthHeaders() });
      setHierarchy(res.data.hierarchy || []);
      
      // Auto-expand portfolios
      const initialExpanded = {};
      (res.data.hierarchy || []).forEach(p => {
        initialExpanded[p.id] = true;
      });
      setExpanded(initialExpanded);
    } catch (err) {
      console.error('Failed to load hierarchy:', err);
      toast.error('Failed to load portfolio hierarchy');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Sorted hierarchy (applies to filtered results)
  const sortedHierarchy = useMemo(() => {
    const sortNodes = (nodes) => {
      const sorted = [...nodes].sort((a, b) => {
        let aVal, bVal;
        
        switch (sortBy) {
          case 'name':
            aVal = a.name?.toLowerCase() || '';
            bVal = b.name?.toLowerCase() || '';
            break;
          case 'status':
            aVal = a.status || '';
            bVal = b.status || '';
            break;
          case 'programs':
            aVal = a.children?.length || 0;
            bVal = b.children?.length || 0;
            break;
          case 'budget':
            aVal = a.budget || 0;
            bVal = b.budget || 0;
            break;
          default:
            aVal = a.name?.toLowerCase() || '';
            bVal = b.name?.toLowerCase() || '';
        }
        
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
      
      // Also sort children recursively
      return sorted.map(node => ({
        ...node,
        children: node.children ? sortNodes(node.children) : []
      }));
    };
    
    return sortNodes(filteredHierarchy);
  }, [filteredHierarchy, sortBy, sortOrder]);
  
  // Toggle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  
  // Toggle expand
  const handleToggle = (nodeId) => {
    setExpanded(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };
  
  // Select node
  const handleSelect = (node) => {
    setSelectedNode(node);
  };
  
  // Edit node
  const handleEdit = (node) => {
    toast.info(`Edit ${node.type}: ${node.name} (coming soon)`);
  };
  
  // Inline edit handler
  const handleInlineEdit = async (nodeId, nodeType, updates) => {
    try {
      let endpoint = '';
      if (nodeType === 'portfolio') {
        endpoint = `/portfolio-mgmt/portfolios/${nodeId}`;
      } else if (nodeType === 'program') {
        endpoint = `/portfolio-mgmt/programs/${nodeId}`;
      } else if (nodeType === 'project') {
        // Projects are updated via projects API
        endpoint = `/projects/${nodeId}`;
      }
      
      await axios.patch(`${API_BASE}${endpoint}`, updates, { headers: getAuthHeaders() });
      toast.success('Updated successfully');
      loadHierarchy();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };
  
  // Navigate from details panel
  const handleNavigate = (target) => {
    if (target.type === 'project-dashboard') {
      navigate(`/projects/${target.id}/dashboard`);
    } else if (target.type === 'settings') {
      navigate(`/settings/${target.nodeType}/${target.id}`);
    } else if (target.type === 'requirements') {
      // Navigate to Requirements/Scoping workspace with project filter
      navigate(`/scope/workspace?projectId=${target.id}`);
    } else if (target.type === 'financial') {
      // Navigate to Financial module with project filter
      navigate(`/financial?projectId=${target.id}`);
    } else {
      // Select the node
      handleSelect(target);
      setExpanded(prev => ({ ...prev, [target.id]: true }));
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 p-6" data-testid="portfolio-hierarchy">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portfolio Hierarchy</h1>
          <p className="text-gray-500">Manage portfolios, programs, and projects</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadHierarchy}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setCreateModal({ open: true, type: 'portfolio', parentId: null })}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4" />
            New Portfolio
          </button>
        </div>
      </div>
      
      {/* Statistics Bar */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Building className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.portfolioCount}</p>
            <p className="text-sm text-gray-500">Portfolios</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Folder className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.programCount}</p>
            <p className="text-sm text-gray-500">Programs</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <FileText className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.projectCount}</p>
            <p className="text-sm text-gray-500">Projects</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.atRiskCount}</p>
            <p className="text-sm text-gray-500">At Risk</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4 flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <XCircle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.criticalCount}</p>
            <p className="text-sm text-gray-500">Critical</p>
          </div>
        </div>
      </div>
      
      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search portfolios, programs, projects..."
              className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="hierarchy-search"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${showFilters ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {(statusFilter !== 'all' || healthFilter !== 'all') && (
              <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {[statusFilter !== 'all', healthFilter !== 'all'].filter(Boolean).length}
              </span>
            )}
          </button>
          
          {/* Expand/Collapse Controls */}
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <button
              onClick={expandAll}
              className="flex items-center gap-1 px-2 py-1 text-sm hover:bg-gray-100 rounded"
              title="Expand All"
            >
              <ChevronsDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => expandToLevel(1)}
              className="flex items-center gap-1 px-2 py-1 text-sm hover:bg-gray-100 rounded"
              title="Show Portfolios + Programs"
            >
              L2
            </button>
            <button
              onClick={() => expandToLevel(0)}
              className="flex items-center gap-1 px-2 py-1 text-sm hover:bg-gray-100 rounded"
              title="Show Portfolios Only"
            >
              L1
            </button>
            <button
              onClick={collapseAll}
              className="flex items-center gap-1 px-2 py-1 text-sm hover:bg-gray-100 rounded"
              title="Collapse All"
            >
              <ChevronsUp className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Filter Options */}
        {showFilters && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Health</label>
              <select
                value={healthFilter}
                onChange={(e) => setHealthFilter(e.target.value)}
                className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Health</option>
                <option value="green">On Track</option>
                <option value="at-risk">At Risk</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            {(statusFilter !== 'all' || healthFilter !== 'all') && (
              <button
                onClick={() => { setStatusFilter('all'); setHealthFilter('all'); }}
                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
        
        {/* Search Results Summary */}
        {(searchTerm || statusFilter !== 'all' || healthFilter !== 'all') && (
          <div className="mt-3 pt-3 border-t text-sm text-gray-500">
            Showing {filteredHierarchy.length} portfolio(s) matching your criteria
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tree View */}
        <div className="lg:col-span-2 bg-white rounded-lg border p-4">
          {/* Sorting Controls */}
          <div className="flex items-center gap-2 mb-4 pb-3 border-b">
            <span className="text-xs font-medium text-gray-500 uppercase">Sort by:</span>
            <button
              onClick={() => handleSort('name')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${sortBy === 'name' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid="sort-by-name"
            >
              Name
              {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
            <button
              onClick={() => handleSort('status')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${sortBy === 'status' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid="sort-by-status"
            >
              Status
              {sortBy === 'status' && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
            <button
              onClick={() => handleSort('programs')}
              className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${sortBy === 'programs' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid="sort-by-programs"
            >
              Children
              {sortBy === 'programs' && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
            </button>
          </div>
          
          {sortedHierarchy.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700">No Portfolios Yet</h3>
              <p className="text-gray-500 mb-4">Create your first portfolio to get started.</p>
              <button
                onClick={() => setCreateModal({ open: true, type: 'portfolio', parentId: null })}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Create Portfolio
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedHierarchy.map(node => (
                <TreeNode
                  key={node.id}
                  node={node}
                  level={0}
                  expanded={expanded}
                  onToggle={handleToggle}
                  onSelect={handleSelect}
                  onEdit={handleEdit}
                  onInlineEdit={handleInlineEdit}
                  selectedId={selectedNode?.id}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Details Panel */}
        <div>
          {selectedNode ? (
            <DetailsPanel
              node={selectedNode}
              onClose={() => setSelectedNode(null)}
              onNavigate={handleNavigate}
            />
          ) : (
            <div className="bg-white rounded-lg border p-6 text-center">
              <Building className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Select an item from the tree to view details</p>
            </div>
          )}
          
          {/* Quick Actions */}
          {selectedNode && selectedNode.type !== 'project' && (
            <div className="mt-4 bg-white rounded-lg border p-4">
              <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
              <button
                onClick={() => setCreateModal({
                  open: true,
                  type: selectedNode.type === 'portfolio' ? 'program' : 'project',
                  parentId: selectedNode.id
                })}
                className="w-full py-2 border-2 border-dashed rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add {selectedNode.type === 'portfolio' ? 'Program' : 'Project'}
              </button>
              
              {/* R3-001: Move button for projects and programs */}
              {selectedNode.type !== 'portfolio' && (
                <button
                  onClick={() => setMoveModal({ open: true, item: selectedNode })}
                  className="w-full mt-2 py-2 border rounded-lg text-gray-600 hover:border-amber-400 hover:text-amber-600 flex items-center justify-center gap-2"
                >
                  <ArrowUpDown className="w-4 h-4" />
                  Move to Different {selectedNode.type === 'project' ? 'Program' : 'Portfolio'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Create Modal */}
      <CreateModal
        isOpen={createModal.open}
        type={createModal.type}
        parentId={createModal.parentId}
        onClose={() => setCreateModal({ open: false, type: null, parentId: null })}
        onSuccess={loadHierarchy}
      />
      
      {/* R3-001: Move Modal */}
      <MoveModal
        isOpen={moveModal.open}
        item={moveModal.item}
        programs={allPrograms}
        portfolios={allPortfolios}
        onClose={() => setMoveModal({ open: false, item: null })}
        onSuccess={loadHierarchy}
      />
    </div>
  );
};

export default PortfolioHierarchy;
